/**
 * @file index.tsx
 *
 * @fileoverview Renders the job search Meta tags (including canonical URLs).
 * For canonical tags on the job search the face order is respected. if an organization is selected the canonical of the organization profile should be used,
 * and if a job is selected the URL of that job should be used.
 *
 * The usage of key="canonical" is important to avoid duplicate tags.
 */
import Head from 'next/head';
import React, { useContext, useMemo } from 'react';
import { replaceSegmentsWithRightOrder } from '~/jobiqo/components/Search/utils/searchHelpers';
import { AppContext, PathContext } from '~/jobiqo/context/app';
import { useFrontendConfig } from '~/jobiqo/context/frontendConfig';
import { AppContextInterface, PathContextInterface } from '~/jobiqo/models/app';
import { OG_IMAGE_META_NAME } from '~/jobiqo/components/Metatag/MetatagDefault';
import { Job } from '~/jobiqo/types/types';
import { withLocalePrefix } from '~/jobiqo/utils/links';

interface JobSearchMetatagsProps {
  /**
   * Current job object.
   */
  currentJob?: Job;
  /**
   * List of jobs.
   */
  jobs: any;
  /**
   * Search page URL. Defaults to the configured jobs page.
   */
  searchUrl?: string;
}

export default function JobSearchMetatags({
  currentJob,
  jobs,
  searchUrl,
}: JobSearchMetatagsProps) {
  const pathContext: PathContextInterface = useContext(PathContext);
  const { locale }: AppContextInterface = useContext(AppContext);
  const { APP, JOBS } = useFrontendConfig();
  const CLEAN_FACETS_ORDER = JOBS.SEO.CLEAN_FACETS_ORDER;
  const JOBS_PAGE = searchUrl ?? JOBS.JOBS_PAGE;
  const LOCALES = APP.LOCALES;
  const JOB_SEARCH_QUERYSTRING_INDEX = JOBS.JOB_SEARCH_QUERYSTRING_INDEX;
  const NO_INDEX_EMPTY_PAGE = JOBS.SEO.NO_INDEX_EMPTY_PAGE;
  const JOB_SEARCH_NOINDEX_AND_NO_CANONICAL_FOR_JOBID =
    JOBS.JOB_SEARCH_NOINDEX_AND_NO_CANONICAL_FOR_JOBID;
  const canonicalPath = pathContext.translatedPath || pathContext.path;

  // If a job is selected, the canonical should be the URL of that job.
  // Jobs are not translated, so it uses the default locale.
  const jobUrl = withLocalePrefix(
    `${pathContext.host}${currentJob?.url.path}`,
    pathContext.host,
    LOCALES,
    APP.DEFAULT_LOCALE,
  );

  const SEOShareImage = useMemo(() => jobs?.seoSearchContent?.shareImage, [
    jobs?.seoSearchContent?.shareImage,
  ]);

  // Check the order of the facets in the querystring.
  // if e.g. the order is /jobs/computer-software/full-time the canonical should be /jobs/full-time/computer-software
  // because that is the order set in the config.
  const canonicalSubPathOrder = replaceSegmentsWithRightOrder(
    canonicalPath,
    jobs,
    JOBS_PAGE,
    CLEAN_FACETS_ORDER,
  );
  const orderedCanonical = withLocalePrefix(
    `${pathContext.host}${canonicalSubPathOrder}`,
    pathContext.host,
    LOCALES,
    locale,
  );

  // If it is a fulltext search like jobs?search=keyword
  // We want to make sure that search is the only thing in the querystring
  // (taxonomies, and other things are not there).
  const isFulltextSearchOnly =
    typeof pathContext.querystring['search'] === 'string' &&
    Object.keys(pathContext.querystring).length === 1;
  let canonicalSearchLink =
    JOB_SEARCH_QUERYSTRING_INDEX && isFulltextSearchOnly && pathContext.host
      ? withLocalePrefix(
          pathContext.host + canonicalPath,
          pathContext.host,
          LOCALES,
          locale,
        )
      : null;

  const hasJobIdQueryParam =
    typeof pathContext.querystring?.jobId === 'string' &&
    pathContext.querystring.jobId.length > 0;

  const shouldNoindexAndHideCanonicalForJobId =
    JOB_SEARCH_NOINDEX_AND_NO_CANONICAL_FOR_JOBID && hasJobIdQueryParam;

  const shouldRenderNoIndex =
    (NO_INDEX_EMPTY_PAGE && jobs?.pages?.length === 0) ||
    shouldNoindexAndHideCanonicalForJobId;

  return (
    <Head>
      {shouldRenderNoIndex && <meta name="robots" content="noindex" />}
      {!shouldNoindexAndHideCanonicalForJobId &&
        orderedCanonical &&
        !currentJob && (
          <link rel="canonical" href={orderedCanonical} key="canonical" />
        )}
      {!shouldNoindexAndHideCanonicalForJobId &&
        canonicalSearchLink &&
        !currentJob && (
          <link rel="canonical" href={canonicalSearchLink} key="canonical" />
        )}
      {!shouldNoindexAndHideCanonicalForJobId && currentJob && (
        <>
          <meta property="og:url" content={jobUrl} />
          <link rel="canonical" href={jobUrl} key="canonical" />
        </>
      )}
      {SEOShareImage && (
        <meta
          key={OG_IMAGE_META_NAME}
          name={OG_IMAGE_META_NAME}
          property={OG_IMAGE_META_NAME}
          content={SEOShareImage.url}
        />
      )}
    </Head>
  );
}
